import { useForm, Controller } from 'react-hook-form';
import { FieldConfig, FormConfig } from '@/school-management/config/formConfig';
import Input from '@/components/school-management/Input/Input';
import ToggleSwitch from '@/components/school-management/Toggle/Toggle';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import MultiSelectDropDown, {
  MultiSelectOption,
} from '@/components/school-management/MultiSelectDropDown/MultiSelectDropDown';
import TextArea from '@/components/school-management/TextArea/TextArea';
import Button from '@/components/school-management/Button/Button';
import SearchBar from '@/components/school-management/SearchBar/SearchBar';
import TableNavFilter from '@/components/school-management/TableNavFilter/TableNavFilter';
import Tag from '@/components/school-management/Tag/Tag';
import Title from '@/components/school-management/TitleStyles/title';
import styles from './DynamicForm.module.scss';
import React, { useState, useEffect } from 'react';
import Popup from '@/components/school-management/Popup/Popup';
import { useNavigate } from 'react-router-dom';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

export interface DynamicFormProps {
  config: FormConfig;
  initialValues?: Record<string, any>;
  errors?: Record<string, string>;
  onCancel?: () => void;
  onSubmit?: (values: any) => void;
  onValuesChange?: (values: any) => void;
  className?: string;
  showPopupOnSubmit?: boolean;
  isreturn?: boolean;
  showPopupOk?: boolean;
  popupType?: 'newAccount' | 'newRegion' | 'changeStatus' | 'saved';
  popupData?: {
    regionName?: string;
    adminName?: string;
  };
}

const DynamicForm: React.FC<DynamicFormProps & { className?: string }> = ({
  config,
  initialValues,
  errors,
  className,
  showPopupOnSubmit = true,
  onCancel,
  onSubmit,
  onValuesChange,
  isreturn,
  showPopupOk = false,
  popupType = 'saved',
  popupData,
}) => {
  const { t } = useAdminTranslation();
  const { control, handleSubmit, watch, setValue, formState, reset } = useForm({ mode: 'onBlur' });
  const [showPopup, setShowPopup] = useState(false);
  const [popupOk, setPopupOk] = useState(showPopupOk);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [showDealOffConfirm, setShowDealOffConfirm] = useState(false);
  const [pendingToggleOff, setPendingToggleOff] = useState<string | null>(null);
  const [isTermsAgreed, setIsTermsAgreed] = useState(false);
  const navigate = useNavigate();
  const [adminCount, setAdminCount] = useState(1);
  const formValues = watch();
  const { user } = useAppSelector(authSelector);
  const schoolName = user?.metadata?.organizationDetails?.organizationName;

  useEffect(() => {
    if (initialValues && !formState.isDirty) {
      reset(initialValues);
    }
  }, [initialValues, formState.isDirty, reset]);

  useEffect(() => {
    onValuesChange?.(formValues);
  }, [formValues, onValuesChange]);

  const resolveValidationMessage = (override: string | undefined, fallback: string): string => {
    if (!override) return fallback;
    const looksLikeI18nKey = /^[\w.-]+$/i.test(override) && override.includes('.');
    return looksLikeI18nKey ? t(override).toString() : override;
  };

  /** Pass through i18n; missing keys are returned as-is by i18next. */
  const translate = (value: string | undefined): string | undefined =>
    value ? t(value) ?? value : value;

  const addAdminFields = () => {
    const newIndex = adminCount;
    const adminFields = config.fields.filter(
      field => field.isAdminGroup && field.adminIndex === 0 && field.name.startsWith('admins.0.')
    );

    const newFields = adminFields.map(field => ({
      ...field,
      name: field.name.replace('admins.0', `admins.${newIndex}`),
      adminIndex: newIndex,
      // Ensure validation rules are copied
      validation: {
        ...field.validation,
        required: field.validation?.required || false,
        pattern: field.validation?.pattern,
      },
    }));

    // Insert new fields before the separator
    const separatorIndex = config.fields.findIndex(
      f => f.type === 'separator' && (f.name === 'separator-3' || f.name === 'separator-3-create')
    );
    if (separatorIndex !== -1) {
      config.fields.splice(separatorIndex, 0, ...newFields);
    }

    // Register the new fields with react-hook-form
    newFields.forEach(field => {
      if (field.validation?.required) {
        setValue(`admins.${newIndex}.${field.name.split('.').pop()}`, '', {
          shouldValidate: true,
        });
      }
    });

    setAdminCount(prev => prev + 1);
  };

  const [isSmallScreen, setIsSmallScreen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 1280 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 1280);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderField = (field: FieldConfig) => {
    if (field.conditionalDisplay && !field.conditionalDisplay(formValues)) {
      return null;
    }
    const gridSpan = (isSmallScreen && field.smGridSpan ? field.smGridSpan : field.gridSpan) || 12;
    const wrapperClass = `${styles[`col-span-${gridSpan}`]} ${styles.fieldWrapper} ${field.type === 'title' ? 'title' : ''}}}`;

    switch (field.type) {
      case 'text':
      case 'date':
      case 'email':
      case 'number':
      case 'password':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || ''
              }
              rules={{
                required: field.validation?.required
                  ? resolveValidationMessage(
                      field.validation?.messages?.required,
                      t('dynamicForm.validation.required').toString()
                    )
                  : false,
                minLength: field.minLength
                  ? {
                      value: field.minLength,
                      message: resolveValidationMessage(
                        field.validation?.messages?.minLength,
                        t('dynamicForm.validation.minLength', {
                          length: field.minLength,
                        }).toString()
                      ),
                    }
                  : undefined,
                maxLength: field.maxLength
                  ? {
                      value: field.maxLength,
                      message: resolveValidationMessage(
                        field.validation?.messages?.maxLength,
                        t('dynamicForm.validation.maxLength', {
                          length: field.maxLength,
                        }).toString()
                      ),
                    }
                  : undefined,
                min: field.min
                  ? {
                      value: field.min,
                      message: resolveValidationMessage(
                        field.validation?.messages?.min,
                        t('dynamicForm.validation.min', { value: field.min }).toString()
                      ),
                    }
                  : undefined,
                max: field.max
                  ? {
                      value: field.max,
                      message: resolveValidationMessage(
                        field.validation?.messages?.max,
                        t('dynamicForm.validation.max', { value: field.max }).toString()
                      ),
                    }
                  : undefined,
                pattern:
                  field.type === 'email'
                    ? {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: resolveValidationMessage(
                          field.validation?.messages?.email,
                          t('dynamicForm.validation.email').toString()
                        ),
                      }
                    : field.validation?.pattern
                      ? {
                          value: field.validation.pattern,
                          message: resolveValidationMessage(
                            field.validation?.messages?.pattern,
                            t('dynamicForm.validation.invalidFormat').toString()
                          ),
                        }
                      : undefined,
              }}
              render={({ field: inputField, fieldState }) => (
                <>
                  <Input
                    {...inputField}
                    type={field.type}
                    label={translate(field.label)}
                    placeholder={translate(field.placeholder)}
                    disabled={field.disabled}
                    min={field.min}
                    max={field.max}
                    minLength={field.minLength}
                    maxLength={field.enforceMaxLength === false ? undefined : field.maxLength}
                    showCharCount={typeof field.maxLength === 'number'}
                    style={field.style}
                    onChange={e => {
                      const maybeTransformed = field.events?.onChange?.(e.target.value, formValues);
                      if (typeof maybeTransformed === 'string') {
                        inputField.onChange(maybeTransformed);
                      } else {
                        inputField.onChange(e);
                      }
                    }}
                  />
                  {formState.errors[field.name] && (
                    <div className={styles.errorMessage}>
                      {formState.errors[field.name]?.message as string}
                    </div>
                  )}
                  {!formState.errors[field.name] && errors?.[field.name] && (
                    <div className={styles.errorMessage}>{errors?.[field.name]}</div>
                  )}
                </>
              )}
            />
          </div>
        );

      case 'title':
        return (
          <div key={field.name} className={`${styles.fieldWrapper}`}>
            <Title
              title={translate(field.label)}
              subTitle={field.subTitle}
              style={field.style}
              buttonText={field.buttonText}
              linkTo={field.linkTo}
              onClick={() => field.events?.onChange?.(null, formValues, addAdminFields)}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || ''
              }
              render={({ field: textAreaField }) => (
                <>
                  <Title subTitle={translate(field.label)} />
                  <TextArea
                    {...textAreaField}
                    placeholder={translate(field.placeholder)}
                    onChange={e => {
                      textAreaField.onChange(e.target.value);
                      field.events?.onChange?.(e.target.value, formValues);
                    }}
                  />
                </>
              )}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || ''
              }
              render={({ field: selectField }) => (
                <DropDown
                  {...selectField}
                  label={translate(field.label)}
                  withHeader={field.withHeader}
                  options={(field.options || []).map(opt => ({
                    ...opt,
                    picture: opt.picture as
                      | 'path'
                      | 'pathActive'
                      | 'dropDown'
                      | 'dropDownV'
                      | 'VIcon'
                      | 'Search'
                      | 'ArrowLeft'
                      | 'separator'
                      | 'toggleOn'
                      | 'toggleOff'
                      | 'logo'
                      | 'portuguese'
                      | undefined,
                  }))}
                  onChange={(value: Option) => {
                    selectField.onChange(value);
                    field.events?.onChange?.(value, formValues);
                  }}
                />
              )}
            />
          </div>
        );

      case 'multi-select':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || []
              }
              rules={{
                validate: field.validation?.required
                  ? (v: any) =>
                      (Array.isArray(v) && v.length > 0) ||
                      t('dynamicForm.validation.required').toString()
                  : undefined,
              }}
              render={({ field: multiSelectField }) => (
                <MultiSelectDropDown
                  {...multiSelectField}
                  label={translate(field.label)}
                  placeholder={translate(field.placeholder)}
                  searchPlaceholder={field.searchPlaceholder}
                  withHeader={field.withHeader}
                  showSelectAll={field.showSelectAll !== false}
                  maxHeight={field.maxHeight}
                  maxDisplayItems={field.maxDisplayItems}
                  showAllSelected={field.showAllSelected}
                  maxSelections={field.maxSelections}
                  onLoadMore={field.onLoadMore}
                  hasMore={field.hasMore}
                  isLoadingMore={field.isLoadingMore}
                  enableAutocomplete={field.enableAutocomplete}
                  organizationId={field.organizationId}
                  autocompleteType={field.autocompleteType}
                  excludeValues={field.excludeValues}
                  isLoadingOptions={field.isLoadingOptions}
                  autocompleteStartOptions={(
                    field.autocompleteStartOptions ??
                    field.options ??
                    []
                  ).map(
                    opt =>
                      ({
                        ...opt,
                        picture: (opt as { picture?: string }).picture as
                          | 'path'
                          | 'pathActive'
                          | 'dropDown'
                          | 'dropDownV'
                          | 'VIcon'
                          | 'Search'
                          | 'ArrowLeft'
                          | 'separator'
                          | 'toggleOn'
                          | 'toggleOff'
                          | 'logo'
                          | 'portuguese'
                          | undefined,
                      }) as MultiSelectOption
                  )}
                  options={(field.options || []).map(opt => ({
                    ...opt,
                    picture: opt.picture as
                      | 'path'
                      | 'pathActive'
                      | 'dropDown'
                      | 'dropDownV'
                      | 'VIcon'
                      | 'Search'
                      | 'ArrowLeft'
                      | 'separator'
                      | 'toggleOn'
                      | 'toggleOff'
                      | 'logo'
                      | 'portuguese'
                      | undefined,
                  }))}
                  onChange={(values: MultiSelectOption[]) => {
                    let limitedValues = values;
                    if (field.maxSelections && values.length > field.maxSelections) {
                      limitedValues = values.slice(0, field.maxSelections);
                    }

                    setValue(field.name, limitedValues, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                    field.events?.onChange?.(limitedValues, formValues);
                  }}
                />
              )}
            />
            {formState.errors[field.name] && (
              <div className={styles.errorMessage}>
                {formState.errors[field.name]?.message as string}
              </div>
            )}
          </div>
        );

      case 'toggle':
        if (field.name === 'toggleDeal') {
          return null;
        }
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || false
              }
              render={({ field: toggleField }) => (
                <ToggleSwitch
                  linkTo={field.linkTo}
                  buttonText={field.buttonText}
                  label={translate(field.label)}
                  subTitle={field.subTitle}
                  checked={!!toggleField.value}
                  onChange={() => {
                    if (field.name === 'toggle') {
                      if (toggleField.value) {
                        // If currently ON, show confirmation before turning OFF
                        setPendingToggleOff('toggle');
                        setShowDealOffConfirm(true);
                      } else {
                        // If currently OFF, turn ON without confirmation
                        setValue('toggle', true);
                      }
                      return;
                    }

                    const nextValue = !toggleField.value;
                    toggleField.onChange(nextValue);
                    field.events?.onChange?.(nextValue, formValues);
                  }}
                />
              )}
            />
          </div>
        );

      case 'segmented-toggle':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || (field.options && field.options[0]?.value) || ''
              }
              render={({ field: segField }) => (
                <ToggleSwitch
                  label={translate(field.label)}
                  subTitle={field.subTitle}
                  options={field.options}
                  value={segField.value}
                  onChange={val => {
                    segField.onChange(val);
                    field.events?.onChange?.(val, formValues);
                  }}
                />
              )}
            />
          </div>
        );

      case 'dropdown':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || null
              }
              render={({ field: dropdownField }) => (
                <DropDown
                  {...dropdownField}
                  label={translate(field.label)}
                  withHeader={field.withHeader}
                  options={(field.options || []).map(opt => ({
                    ...opt,
                    picture: opt.picture as
                      | 'path'
                      | 'pathActive'
                      | 'dropDown'
                      | 'dropDownV'
                      | 'VIcon'
                      | 'Search'
                      | 'ArrowLeft'
                      | 'separator'
                      | 'toggleOn'
                      | 'toggleOff'
                      | 'logo'
                      | 'portuguese'
                      | undefined,
                  }))}
                  value={dropdownField.value || null}
                  onChange={(value: Option) => {
                    dropdownField.onChange(value);
                    field.events?.onChange?.(value, formValues);
                  }}
                />
              )}
            />
          </div>
        );

      case 'tags':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || 'INACTIVE'
              }
              render={({ field: tagField }) => (
                <>
                  <Title title={translate(field.label)} />
                  <Tag type={tagField.value || 'INACTIVE'} />
                </>
              )}
            />
          </div>
        );

      case 'button':
        // Special handling for Cancel and Submit
        if (field.name.toLowerCase().includes('cancel')) {
          return (
            <div key={field.name} className={wrapperClass}>
              <Button
                key={field.name}
                type='button'
                onClick={() => {
                  reset(); // Reset form data
                  if (onCancel) {
                    onCancel();
                  } else {
                    field.events?.onChange?.(null, formValues);
                  }
                }}
                loading={field.loading}
                disabled={field.disabled}
                block={field.block}
                secondary={field.secondary}
                isTransparent={field.isTransparent}
                style={field.style}
              >
                {translate(field.label)}
              </Button>
            </div>
          );
        }
        if (
          field.name.toLowerCase().includes('submit') ||
          field.name.toLowerCase().includes('save')
        ) {
          return (
            <div key={field.name} className={wrapperClass}>
              <Button
                key={field.name}
                type='button'
                onClick={handleSubmit(data => {
                  if (showPopupOnSubmit) {
                    setPendingFormData(data);
                    setShowPopup(true);
                  } else if (showPopupOk) {
                    setPendingFormData(data);
                    setPopupOk(true);
                  } else {
                    if (onSubmit) {
                      onSubmit(data);
                    } else if (config.onSubmit) {
                      config.onSubmit(data);
                    }
                  }
                })}
                loading={field.loading}
                disabled={
                  field.disabled || !formState.isDirty || Object.keys(formState.errors).length > 0
                }
                block={field.block}
                secondary={field.secondary}
                isTransparent={field.isTransparent}
                style={{
                  ...field.style,
                  background:
                    formState.isDirty && Object.keys(formState.errors).length === 0
                      ? '#1463b0'
                      : field.style?.background || '#B5B5B5',
                  color:
                    formState.isDirty && Object.keys(formState.errors).length === 0
                      ? '#ffffff'
                      : field.style?.color,
                }}
              >
                {translate(field.label)}
              </Button>
            </div>
          );
        }
        // Default button logic
        return (
          <div key={field.name} className={wrapperClass}>
            <Button
              key={field.name}
              type='button'
              onClick={() => field.events?.onChange?.(null, formValues)}
              loading={field.loading}
              disabled={field.disabled}
              block={field.block}
              secondary={field.secondary}
              isTransparent={field.isTransparent}
              isBack={field.name === 'back'}
              isDelete={field.name === 'delete'}
              style={field.style}
            >
              {translate(field.label)}
            </Button>
          </div>
        );

      case 'search':
        return (
          <div key={field.name} className={wrapperClass}>
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              defaultValue={
                initialValues && initialValues[field.name] !== undefined
                  ? initialValues[field.name]
                  : field.defaultValue || ''
              }
              render={({ field: searchField }) => (
                <SearchBar
                  value={searchField.value}
                  onChange={e => {
                    searchField.onChange(e);
                    field.events?.onChange?.(e.target.value, formValues);
                  }}
                  onFocus={field.events?.onFocus}
                  onBlur={field.events?.onBlur}
                  placeholder={translate(field.placeholder) || ''}
                />
              )}
            />
          </div>
        );

      case 'tab':
        return (
          <div key={field.name} className={wrapperClass}>
            {(field.options || []).map(option => {
              const isActive = formValues[field.name] === option.value;
              const status: 'Default' | 'Hover' | 'Active' | 'Disabled' = isActive
                ? 'Active'
                : 'Default';

              return (
                <TableNavFilter
                  key={option.value}
                  label={option.label}
                  status={status}
                  onClick={() => {
                    setValue(field.name, option.value);
                    field.events?.onChange?.(option.value, formValues);
                  }}
                />
              );
            })}
          </div>
        );

      case 'separator':
        return (
          <div key={field.name} className={wrapperClass}>
            <div className={styles.separatorGap}>
              <div className={styles.separator} />
            </div>
          </div>
        );

      case 'ExtraSeparator':
        return (
          <div key={field.name} className={wrapperClass}>
            <div style={{ padding: `${field.spacing || 40}px 0` }} />
          </div>
        );

      default:
        return null;
    }
  };

  const getPopupConfig = () => {
    switch (popupType) {
      case 'newRegion':
        return {
          title: t('dynamicForm.popups.newRegion.title').replace(
            '%regionName%',
            popupData?.regionName || ''
          ),
          message: t('dynamicForm.popups.newRegion.subTitle').replace(
            '%region admin first and last name%',
            popupData?.adminName || ''
          ),
          confirmLabel: t('dynamicForm.popups.newRegion.ok'),
          showCheckbox: false,
          singleButtonOnly: true,
        };
      case 'changeStatus':
        return {
          title: t('dynamicForm.popups.changeStatus.title'),
          message: t('dynamicForm.popups.changeStatus.subTitle'),
          confirmLabel: t('dynamicForm.popups.changeStatus.confirm'),
          cancelLabel: t('dynamicForm.popups.changeStatus.cancel'),
          showCheckbox: false,
        };
      case 'newAccount':
        return {
          title: t('dynamicForm.popups.newAccount.title'),
          message: t('dynamicForm.popups.newAccount.subTitle'),
          confirmLabel: t('dynamicForm.popups.newAccount.confirm'),
          cancelLabel: t('dynamicForm.popups.newAccount.cancel'),
          showCheckbox: true,
        };
      default:
        return {
          title: t('dynamicForm.popups.saved.title'),
          message: t('dynamicForm.popups.saved.subTitle'),
          confirmLabel: t('ok'),
          showCheckbox: false,
          singleButtonOnly: true,
        };
    }
  };

  useEffect(() => {
    console.log('getPopupConfig', getPopupConfig());
  }, [getPopupConfig]);
  return (
    <>
      <div className={styles.header}>
        <div className={styles.backButton}>
          <Button
            secondary
            isBack
            onBack={() => {
              if (onCancel) {
                onCancel();
              } else {
                navigate(-1);
              }
            }}
          >
            {t('common.back')}
          </Button>
        </div>
        <div className={styles.titleRow}>
          <h1>
            {translate(
              config.fields.find(field => field.type === 'title' && field.name === 'main-title')
                ?.MainTitle
            )}
          </h1>
          <span className={styles.schoolName}>{schoolName}</span>
          {config.fields.find(field => field.type === 'toggle' && field.name === 'toggleDeal') && (
            <>
              {' '}
              <Tag type={watch('toggle') ? 'ACTIVE' : 'INACTIVE'} />
              <div className={styles.toggleContainer}>
                <ToggleSwitch
                  checked={watch('toggle')}
                  onChange={() => {
                    const currentValue = watch('toggle');
                    if (currentValue) {
                      setPendingToggleOff('toggle');
                      setShowDealOffConfirm(true);
                    } else {
                      setValue('toggle', true);
                    }
                  }}
                />
                <span className={styles.toggleLabel}>Account On</span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.formContainer}>
        <form
          onSubmit={handleSubmit(onSubmit ? onSubmit : config.onSubmit)}
          className={`${styles.formGrid} ${className}`}
        >
          {config.fields.map(field => (
            <React.Fragment key={`${field.name}-${field.adminIndex || 0}`}>
              {renderField(field)}
            </React.Fragment>
          ))}

          {showDealOffConfirm && (
            <Popup
              open={showDealOffConfirm}
              onClose={() => {
                setShowDealOffConfirm(false);
                if (pendingToggleOff) {
                  setValue(pendingToggleOff, true); // Keep it ON
                  setPendingToggleOff(null);
                }
              }}
              title={t('dynamicForm.popups.turnOffDeal.title') || ''}
              message={t('dynamicForm.popups.turnOffDeal.subTitle') || ''}
              confirmLabel={t('dynamicForm.popups.turnOffDeal.turnOff') || ''}
              cancelLabel={t('dynamicForm.popups.turnOffDeal.keepOn') || ''}
              onEnable={() => {
                setShowDealOffConfirm(false);
                if (pendingToggleOff) {
                  setValue(pendingToggleOff, false); // Turn it OFF
                  setPendingToggleOff(null);
                }
              }}
              showCheckbox={false}
            />
          )}

          {showPopup && (
            <Popup
              open={showPopup}
              onClose={() => {
                setShowPopup(false);
                setPendingFormData(null);
              }}
              onEnable={() => {
                setShowPopup(false);
                if (config.onSubmit && pendingFormData) {
                  config.onSubmit(pendingFormData);
                  if (!isreturn) {
                    navigate(-1);
                  } else {
                    onSubmit?.(pendingFormData);
                  }
                }
              }}
              title={t('Terms of Use') || ''}
              message={t('dynamicForm.popups.newAccount.subTitle') || ''}
              confirmLabel={t('dynamicForm.popups.newAccount.confirm') || ''}
              cancelLabel={t('dynamicForm.popups.newAccount.cancel') || ''}
              checked={isTermsAgreed}
              onCheck={setIsTermsAgreed}
              showCheckbox={true}
            />
          )}

          {popupOk && (
            <Popup
              open={popupOk}
              onClose={() => {
                setPopupOk(false);
                setPendingFormData(null);
              }}
              onEnable={() => {
                setPopupOk(false);
                if (config.onSubmit && pendingFormData) {
                  config.onSubmit(pendingFormData);
                  if (!isreturn) {
                    navigate(-1);
                  } else {
                    onSubmit?.(pendingFormData);
                  }
                }
              }}
              {...getPopupConfig()}
            />
          )}
        </form>
      </div>
    </>
  );
};

export default DynamicForm;
